import { hasPaidPlan } from "../utils/subscription";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { NadiCustomEvent, nadiEventKey } from "../utils/nadiCustomEvents";
import { STUDIO_API_BASE_URL } from "../api/studioClient";
const NADI_API_BASE = STUDIO_API_BASE_URL;
const CHANGE_EVENT = "kundlistar-nadi-custom-events-changed";

export function useNadiCustomEvents() {
  const { state: auth } = useAuth();
  const token = auth.token || "";
  const hasPaidAccess = hasPaidPlan(auth.user);
  const activeToken = useRef(token);
  activeToken.current = hasPaidAccess ? token : "";
  const requestNumber = useRef(0);
  const [snapshot, setSnapshot] = useState<{
    owner: string;
    events: NadiCustomEvent[];
  }>({ owner: "", events: [] });
  const [status, setStatus] = useState({
    owner: "",
    loading: false,
    error: "",
  });
  const events =
    snapshot.owner === token && token && hasPaidAccess ? snapshot.events : [];
  const send = useCallback(
    async (method: string, id = "", body?: Omit<NadiCustomEvent, "id">) => {
      if (!token)
        throw new Error("Sign in to save custom events to your profile.");
      if (!hasPaidAccess)
        throw new Error(
          "Custom events are available to paid subscribers only.",
        );
      const response = await fetch(
        `${NADI_API_BASE}/users/nadi-events${id ? "/" + encodeURIComponent(id) : ""}`,
        {
          method,
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          ...(body ? { body: JSON.stringify(body) } : {}),
        },
      );
      const json = await response.json();
      if (!response.ok || !json.success)
        throw new Error(
          json.message || "Unable to load or save custom events.",
        );
      if (activeToken.current !== token)
        throw new Error("Account changed while saving. Please try again.");
      return json.data.events as NadiCustomEvent[];
    },
    [token, hasPaidAccess],
  );
  const reload = useCallback(async () => {
    const number = ++requestNumber.current;
    if (!token || !hasPaidAccess) {
      setSnapshot({ owner: "", events: [] });
      setStatus({ owner: "", loading: false, error: "" });
      return;
    }
    setStatus({ owner: token, loading: true, error: "" });
    try {
      const list = await send("GET");
      if (activeToken.current === token && number === requestNumber.current) {
        setSnapshot({ owner: token, events: list });
        setStatus({ owner: token, loading: false, error: "" });
      }
    } catch (error) {
      if (activeToken.current === token && number === requestNumber.current)
        setStatus({
          owner: token,
          loading: false,
          error: (error as Error).message,
        });
    }
  }, [send, token, hasPaidAccess]);
  useEffect(() => {
    void reload();
    const listener = () => void reload();
    window.addEventListener(CHANGE_EVENT, listener);
    return () => {
      window.removeEventListener(CHANGE_EVENT, listener);
      requestNumber.current++;
    };
  }, [reload]);
  const mutate = useCallback(
    async (method: string, id = "", body?: Omit<NadiCustomEvent, "id">) => {
      ++requestNumber.current;
      const list = await send(method, id, body);
      if (activeToken.current === token) {
        setSnapshot({ owner: token, events: list });
        setStatus({ owner: token, loading: false, error: "" });
        window.dispatchEvent(new Event(CHANGE_EVENT));
      }
      return list;
    },
    [send, token],
  );
  const eventDefinitions = useMemo(
    () =>
      Object.fromEntries(
        events.map((event) => [nadiEventKey(event.id), event]),
      ),
    [events],
  );
  return {
    events,
    eventDefinitions,
    hasPaidAccess,
    signedIn: !!token,
    accountKey: token,
    loading: status.owner === token && status.loading,
    error: status.owner === token ? status.error : "",
    reload,
    saveEvent: async (event: Omit<NadiCustomEvent, "id">, id?: string) => {
      const list = await mutate(id ? "PUT" : "POST", id, event);
      return list.find((item) => item.id === id) || list[list.length - 1];
    },
    deleteEvent: (id: string) => mutate("DELETE", id),
  };
}
export type NadiCustomEventsStore = ReturnType<typeof useNadiCustomEvents>;
